import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, updateDoc, doc, query, orderBy } from 'firebase/firestore';
import {
  Box, Typography, Paper, Chip, CircularProgress,
  IconButton, Divider, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Autocomplete, ToggleButtonGroup, ToggleButton, Tooltip, Container,
  Select, MenuItem, InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LagbasLogo from '../components/LagbasLogo';
import { deptCode } from '../utils/depts';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer, Legend,
} from 'recharts';

// ── Helpers ─────────────────────────────────────────────────────────────────

function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function generateNextMonths(n = 12, offsetMonths = 0) {
  const now = new Date();
  const result = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + offsetMonths + i, 1);
    result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return result;
}

function generateMonths(startMonth, endMonth) {
  if (!startMonth || !endMonth) return [];
  const result = [];
  let [y, m] = startMonth.split('-').map(Number);
  const [ey, em] = endMonth.split('-').map(Number);
  while (y < ey || (y === ey && m <= em)) {
    result.push(`${y}-${String(m).padStart(2, '0')}`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return result;
}

function workdaysInMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  const days = new Date(y, m, 0).getDate();
  let count = 0;
  for (let d = 1; d <= days; d++) {
    const dow = new Date(y, m - 1, d).getDay();
    if (dow !== 0 && dow !== 6) count++;
  }
  return count;
}

// ── Explicit-start column generators ─────────────────────────────────────────

// generateMonthsFrom('YYYY-MM', n) → ['YYYY-MM', ...]
function generateMonthsFrom(startStr, n) {
  let [y, m] = startStr.split('-').map(Number);
  const result = [];
  for (let i = 0; i < n; i++) {
    result.push(`${y}-${String(m).padStart(2, '0')}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return result;
}

function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// Parse HTML week input value 'YYYY-Www' → Monday Date
function parseISOWeekStr(weekStr) {
  const [yearStr, wStr] = weekStr.split('-W');
  const year = Number(yearStr), week = Number(wStr);
  const jan4 = new Date(year, 0, 4);
  const jan4day = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - jan4day + 1 + (week - 1) * 7);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

// generateWeeksFrom('YYYY-Www', n) → week objects
function generateWeeksFrom(startStr, n) {
  const monday = parseISOWeekStr(startStr);
  const result = [];
  for (let i = 0; i < n; i++) {
    const start = new Date(monday);
    start.setDate(monday.getDate() + i * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 4);
    const wn = getISOWeek(start);
    result.push({
      id: `${start.getFullYear()}-W${String(wn).padStart(2, '0')}`,
      label: `v${wn}`,
      monthLabel: start.toLocaleDateString('sv-SE', { month: 'short' }),
      start: new Date(start), end: new Date(end),
    });
  }
  return result;
}

function weekCapacity(week) {
  let days = 0;
  const d = new Date(week.start);
  while (d <= week.end) {
    if (d.getDay() !== 0 && d.getDay() !== 6) days++;
    d.setDate(d.getDate() + 1);
  }
  return days * HOURS_PER_DAY;
}

// generateDaysFrom('YYYY-MM-DD', n) → day objects (workdays only)
function generateDaysFrom(startStr, n) {
  const [y, m, d] = startStr.split('-').map(Number);
  const cur = new Date(y, m - 1, d);
  cur.setHours(0, 0, 0, 0);
  while (cur.getDay() === 0 || cur.getDay() === 6) cur.setDate(cur.getDate() + 1);
  const result = [];
  const dayNames = ['sön', 'mån', 'tis', 'ons', 'tor', 'fre', 'lör'];
  let prevMonth = null;
  while (result.length < n) {
    if (cur.getDay() !== 0 && cur.getDay() !== 6) {
      const isoDate = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
      const isoMonth = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`;
      const isFirstOfMonth = isoMonth !== prevMonth;
      prevMonth = isoMonth;
      result.push({ id: isoDate, date: new Date(cur), isoDate, isoMonth,
        label: `${dayNames[cur.getDay()]} ${cur.getDate()}`,
        monthLabel: cur.toLocaleDateString('sv-SE', { month: 'short', year: '2-digit' }),
        isFirstOfMonth });
    }
    cur.setDate(cur.getDate() + 1);
  }
  return result;
}

// Default window start for today
function getDefaultWindowStart(gran) {
  const now = new Date();
  if (gran === 'month') return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (gran === 'week') {
    const dow = now.getDay();
    const mon = new Date(now);
    mon.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
    mon.setHours(0, 0, 0, 0);
    return `${mon.getFullYear()}-W${String(getISOWeek(mon)).padStart(2, '0')}`;
  }
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// Shift a window-start string forward/back by n column-steps
function shiftWindowStart(start, gran, n) {
  if (gran === 'month') {
    let [y, m] = start.split('-').map(Number);
    m += n;
    while (m > 12) { m -= 12; y++; }
    while (m < 1)  { m += 12; y--; }
    return `${y}-${String(m).padStart(2, '0')}`;
  }
  if (gran === 'week') {
    const mon = parseISOWeekStr(start);
    mon.setDate(mon.getDate() + n * 7);
    return `${mon.getFullYear()}-W${String(getISOWeek(mon)).padStart(2, '0')}`;
  }
  // day: step by workdays
  const [y, m, d] = start.split('-').map(Number);
  const cur = new Date(y, m - 1, d);
  const step = n > 0 ? 1 : -1;
  let rem = Math.abs(n);
  while (rem > 0) {
    cur.setDate(cur.getDate() + step);
    if (cur.getDay() !== 0 && cur.getDay() !== 6) rem--;
  }
  return `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
}

function dayHrsFromMonthData(monthHrs, day) {
  const totalDays = workdaysInMonth(day.isoMonth);
  if (totalDays <= 0) return 0;
  return (monthHrs[day.isoMonth] || 0) / totalDays;
}

// Prorate monthly hours data into a given week (handles week spanning two months)
function weekHrsFromMonthData(monthHrs, week) {
  let total = 0;
  const daysPerMonth = {};
  const d = new Date(week.start);
  while (d <= week.end) {
    if (d.getDay() !== 0 && d.getDay() !== 6) {
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      daysPerMonth[m] = (daysPerMonth[m] || 0) + 1;
    }
    d.setDate(d.getDate() + 1);
  }
  Object.entries(daysPerMonth).forEach(([month, days]) => {
    const totalDays = workdaysInMonth(month);
    if (totalDays > 0) total += (monthHrs[month] || 0) * (days / totalDays);
  });
  return total;
}

function fmtMonth(ym) {
  const names = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  const [y, m] = ym.split('-');
  return names[parseInt(m, 10) - 1] + ' ' + y.slice(2);
}

function fmtSEK(v) {
  if (!v) return '–';
  return Number(v).toLocaleString('sv-SE') + ' kr';
}

// Safely evaluate a numeric formula like "50/2" or "3*8+4"
function evalFormula(str) {
  if (!str && str !== 0) return null;
  const cleaned = String(str).replace(/[^0-9+\-*/().]/g, '');
  if (!cleaned) return null;
  try {
    // eslint-disable-next-line no-new-func
    const result = new Function('return (' + cleaned + ')')();
    return typeof result === 'number' && isFinite(result) && result >= 0 ? result : null;
  } catch { return null; }
}

// Convert val from given unit to hours-per-month for a specific calendar month
function hrsPerMonthFromUnit(val, unit, month) {
  const wd = workdaysInMonth(month);
  if (unit === 'week') return val * (wd / 5);
  if (unit === 'day')  return val * wd;
  return val; // 'month'
}

function utilizationColors(pct) {
  if (pct <= 0)   return { bg: undefined,   bar: '#90CAF9', text: '#888' };
  if (pct < 50)   return { bg: '#E8F5E9',   bar: '#4CAF50', text: '#2E7D32' };
  if (pct < 80)   return { bg: '#E3F2FD',   bar: '#1976D2', text: '#1565C0' };
  if (pct <= 100) return { bg: '#FFF3E0',   bar: '#F57C00', text: '#E65100' };
  return           { bg: '#FFEBEE',   bar: '#D32F2F', text: '#C62828' };
}

const HOURS_PER_DAY = 8;

const STATUS_META = {
  aktiv:     { label: 'Aktivt',   color: '#1565C0', bg: '#E3F2FD' },
  offertfas: { label: 'Offert',   color: '#E65100', bg: '#FFF3E0' },
  pausad:    { label: 'Pausat',   color: '#7B1FA2', bg: '#F3E5F5' },
  avslutad:  { label: 'Avslutat', color: '#607D8B', bg: '#ECEFF1' },
};

const STATUS_ORDER = ['aktiv', 'offertfas', 'pausad', 'avslutad'];

const TEAL_DARK = '#01579B';
const TEAL      = '#0288D1';

// ── Main component ────────────────────────────────────────────────────────────

export default function FramtidN() {
  const navigate = useNavigate();
  const [loading, setLoading]         = useState(true);
  const [projects, setProjects]       = useState([]);
  const [users, setUsers]             = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptFilter, setDeptFilter]   = useState([]);

  const [viewMode, setViewMode]           = useState('plan');   // 'plan' | 'report'
  const [viewDimension, setViewDimension] = useState('person'); // 'person' | 'project'
  const [granularity, setGranularity]     = useState('month');  // 'month' | 'week' | 'day'
  const [windowStart, setWindowStart]     = useState(null);     // null = today; else 'YYYY-MM'/'YYYY-Www'/'YYYY-MM-DD'
  const [colsConfig, setColsConfig]       = useState(() => {
    try { return JSON.parse(localStorage.getItem('framtidn_cols') || 'null') || { month: 4, week: 8, day: 10, report: 12 }; }
    catch { return { month: 4, week: 8, day: 10, report: 12 }; }
  });
  const [reportStart, setReportStart]     = useState(null); // null = today
  const [collapsed, setCollapsed]         = useState(new Set());
  const [projectFilter, setProjectFilter] = useState('');
  const [hideEmpty, setHideEmpty]         = useState(false);
  const [dragSel, setDragSel]             = useState(null);     // { userId, startIdx, endIdx, active }
  const [fiktivDragSel, setFiktivDragSel] = useState(null);    // { projectId, startIdx, endIdx, active }

  // planDialog: { userId, month }
  const [planDialog, setPlanDialog] = useState(null);
  // planForm: { projectId, hoursPerMonth, rangeStart, rangeEnd, note }
  const [planForm, setPlanForm]     = useState({ projectId: '', hoursPerMonth: '', rangeStart: '', rangeEnd: '', note: '' });
  const [planUnit, setPlanUnit]     = useState('month'); // 'month' | 'week' | 'day'
  const [saving, setSaving]         = useState(false);
  const [fiktivDialog, setFiktivDialog] = useState(null); // { projectId, months: ['YYYY-MM',...] }
  const [fiktivInput, setFiktivInput]   = useState('');
  const [fiktivRangeMode, setFiktivRangeMode] = useState('per'); // 'per' | 'total'
  const [fiktivSaving, setFiktivSaving] = useState(false);

  const COLS = colsConfig[granularity] || (granularity === 'month' ? 4 : granularity === 'week' ? 8 : 10);

  function setColsForGranularity(n) {
    const next = { ...colsConfig, [granularity]: Math.max(1, Math.min(granularity === 'month' ? 24 : granularity === 'week' ? 52 : 60, n)) };
    setColsConfig(next);
    localStorage.setItem('framtidn_cols', JSON.stringify(next));
  }

  function setReportCols(n) {
    const next = { ...colsConfig, report: Math.max(1, Math.min(36, n)) };
    setColsConfig(next);
    localStorage.setItem('framtidn_cols', JSON.stringify(next));
  }
  const effectiveStart = windowStart || getDefaultWindowStart(granularity);

  const months = granularity === 'month' ? generateMonthsFrom(effectiveStart, COLS) : null;
  const weeks  = granularity === 'week'  ? generateWeeksFrom(effectiveStart, COLS)  : null;
  const days   = granularity === 'day'   ? generateDaysFrom(effectiveStart, COLS)   : null;

  const load = useCallback(async () => {
    setLoading(true);
    const [projSnap, userSnap, deptSnap] = await Promise.all([
      getDocs(query(collection(db, 'projects'), orderBy('name'))),
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'departments')),
    ]);
    setProjects(projSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stop drag selection on global mouseup (mouse left the table)
  useEffect(() => {
    const stop = () => {
      setDragSel(s => s?.active ? { ...s, active: false } : s);
      setFiktivDragSel(s => s?.active ? { ...s, active: false } : s);
    };
    document.addEventListener('mouseup', stop);
    return () => document.removeEventListener('mouseup', stop);
  }, []);

  // ── Active projects ────────────────────────────────────────────────────────
  const planableStatuses = ['aktiv', 'offertfas', 'pausad'];
  const activeProjects = projects
    .filter(p => planableStatuses.includes(p.status || 'offertfas'))
    .sort((a, b) => {
      const ao = STATUS_ORDER.indexOf(a.status);
      const bo = STATUS_ORDER.indexOf(b.status);
      if (ao !== bo) return ao - bo;
      return (a.name || '').localeCompare(b.name || '', 'sv');
    });

  // ── Person view: planData[userId][month] ──────────────────────────────────
  // Always computed over a wide window (48 months) to support week prorating from any offset
  const PLAN_WINDOW = 48;
  const allMonthsWindow = generateNextMonths(PLAN_WINDOW, -6); // 6 months back, 42 forward

  const planData = {};
  users.forEach(u => {
    planData[u.id] = {};
    allMonthsWindow.forEach(m => {
      planData[u.id][m] = { capacity: workdaysInMonth(m) * HOURS_PER_DAY, total: 0, entries: [] };
    });
  });

  activeProjects.forEach(project => {
    const status = project.status || 'offertfas';
    const latestBudget = (project.budgets || []).at(-1);

    if (latestBudget) {
      const resources  = latestBudget.resources  || [];
      const activities = latestBudget.activities || [];
      // When a period is defined: constrain to it to avoid stale act.months data.
      // When no period: fall back to the full window (budget was created without dates).
      const hasPeriod = latestBudget.startMonth && latestBudget.endMonth;
      const budgetMonths = hasPeriod
        ? generateMonths(latestBudget.startMonth, latestBudget.endMonth)
        : allMonthsWindow;
      resources.forEach(res => {
        if (!res.userId || !planData[res.userId]) return;
        const uid = res.userId;
        activities.filter(a => a.resourceId === res.id).forEach(act => {
          budgetMonths.forEach(m => {
            if (!planData[uid][m]) return; // month outside our 48-month window
            const hrs = parseFloat(act.months?.[m]) || 0;
            if (hrs <= 0) return;
            const cell = planData[uid][m];
            cell.total += hrs;
            const existing = cell.entries.find(e => e.projectId === project.id && e.type === 'budget');
            if (existing) {
              existing.hours += hrs;
            } else {
              cell.entries.push({
                projectId: project.id, projectName: project.name || '(namnlöst)',
                projectNumber: project.projectNumber || '',
                hours: hrs, type: 'budget', status,
              });
            }
          });
        });
      });
    }

    (project.roughPlan || []).forEach(rp => {
      if (!rp.userId || !planData[rp.userId]) return;
      if (!allMonthsWindow.includes(rp.month)) return;
      const hrs = parseFloat(rp.hours) || 0;
      if (hrs <= 0) return;
      const cell = planData[rp.userId][rp.month];
      cell.total += hrs;
      const existing = cell.entries.find(e => e.projectId === project.id && e.type === 'rough');
      if (existing) {
        existing.hours += hrs;
      } else {
        cell.entries.push({
          projectId: project.id, projectName: project.name || '(namnlöst)',
          projectNumber: project.projectNumber || '',
          hours: hrs, type: 'rough', status, roughId: rp.id,
        });
      }
    });
  });

  // ── Project view data ──────────────────────────────────────────────────────
  const projectRows = activeProjects.map(project => {
    const latestBudget = (project.budgets || []).at(-1);
    const resources  = latestBudget?.resources  || [];
    const activities = latestBudget?.activities || [];
    // Constrain to budget period if set (avoids stale act.months data);
    // otherwise use full window for budgets created without explicit dates.
    const hasPeriod = latestBudget?.startMonth && latestBudget?.endMonth;
    const budgetMonths = hasPeriod
      ? generateMonths(latestBudget.startMonth, latestBudget.endMonth)
      : null; // null = no filter

    // Rough plan per user: { userId: { 'YYYY-MM': hours } }
    const roughHrsByUser = {};
    (project.roughPlan || []).forEach(rp => {
      if (!rp.userId) return;
      const hrs = parseFloat(rp.hours) || 0;
      if (hrs <= 0) return;
      if (!roughHrsByUser[rp.userId]) roughHrsByUser[rp.userId] = {};
      roughHrsByUser[rp.userId][rp.month] = (roughHrsByUser[rp.userId][rp.month] || 0) + hrs;
    });

    const resRows = resources.map(res => {
      const user = users.find(u => u.id === res.userId);
      const monthHrs = {}; // budget only
      allMonthsWindow.forEach(m => {
        const include = budgetMonths ? budgetMonths.includes(m) : true;
        monthHrs[m] = include
          ? activities.filter(a => a.resourceId === res.id)
              .reduce((s, a) => s + (parseFloat(a.months?.[m]) || 0), 0)
          : 0;
      });
      const roughHrs = res.userId ? (roughHrsByUser[res.userId] || {}) : {};
      const totalHrs = (budgetMonths || allMonthsWindow).reduce((s, m) => s + (monthHrs[m] || 0), 0);
      const totalVal = totalHrs * (parseFloat(res.hourlyRate) || 0);
      return { res, user, monthHrs, roughHrs, totalHrs, totalVal };
    });

    const totalMonthHrs = {};
    allMonthsWindow.forEach(m => {
      totalMonthHrs[m] = resRows.reduce((s, r) => s + (r.monthHrs[m] || 0), 0);
    });
    const totalBudgetHrs = (budgetMonths || allMonthsWindow).reduce((s, m) => s + (totalMonthHrs[m] || 0), 0);
    const totalBudgetVal = resRows.reduce((s, r) => s + r.totalVal, 0);

    // Users with rough plan but no budget resource entry → extra rows
    const roughOnlyRows = [];
    Object.entries(roughHrsByUser).forEach(([uid, mhrs]) => {
      if (resources.some(r => r.userId === uid)) return; // already covered by resRows
      const user = users.find(u => u.id === uid);
      roughOnlyRows.push({ user, uid, monthHrs: mhrs });
    });

    const roughMonthHrs = {};
    allMonthsWindow.forEach(m => {
      roughMonthHrs[m] = (project.roughPlan || [])
        .filter(rp => rp.month === m)
        .reduce((s, rp) => s + (parseFloat(rp.hours) || 0), 0);
    });
    const totalRoughHrs = allMonthsWindow.reduce((s, m) => s + (roughMonthHrs[m] || 0), 0);

    const fiktivMonthHrs = {};
    allMonthsWindow.forEach(m => { fiktivMonthHrs[m] = parseFloat(project.fiktivPlan?.[m]) || 0; });
    const totalFiktivHrs = allMonthsWindow.reduce((s, m) => s + (fiktivMonthHrs[m] || 0), 0);

    return { project, latestBudget, resRows, roughOnlyRows, totalMonthHrs, roughMonthHrs, totalRoughHrs, fiktivMonthHrs, totalFiktivHrs, totalBudgetHrs, totalBudgetVal };
  });

  // ── Filtered views ─────────────────────────────────────────────────────────
  const pf = projectFilter.trim().toLowerCase();

  const visibleUsers = users
    .filter(u => deptFilter.length === 0 || deptFilter.includes(u.departmentId))
    .filter(u => {
      if (!pf) return true;
      // Show user if they have any entry for a matching project
      const checkMonths = granularity === 'month' ? months : granularity === 'week'
        ? [...new Set(weeks.map(w => weekToMonth(w)))]
        : [...new Set(days.map(d => d.isoMonth))];
      const hasMatch = (checkMonths || allMonthsWindow).some(m => {
        const cell = planData[u.id]?.[m];
        return cell?.entries.some(e =>
          e.projectName.toLowerCase().includes(pf) ||
          e.projectNumber.toLowerCase().includes(pf)
        );
      });
      return hasMatch;
    })
    .filter(u => {
      if (!hideEmpty) return true;
      const hasSomeData = allMonthsWindow.some(m => (planData[u.id]?.[m]?.total || 0) > 0);
      return hasSomeData;
    })
    .sort((a, b) => (a.displayName || a.name || '').localeCompare(b.displayName || b.name || '', 'sv'));

  const visibleProjectRows = projectRows
    .filter(({ project }) => {
      if (!pf) return true;
      return (project.name || '').toLowerCase().includes(pf)
        || (project.projectNumber || '').toLowerCase().includes(pf);
    })
    .filter(({ totalBudgetHrs, totalRoughHrs, totalFiktivHrs }) => {
      if (!hideEmpty) return true;
      return totalBudgetHrs > 0 || totalRoughHrs > 0 || totalFiktivHrs > 0;
    });

  // ── Report data ────────────────────────────────────────────────────────────
  const reportCols = colsConfig.report || 12;
  const reportEffectiveStart = reportStart || getDefaultWindowStart('month');
  const reportMonths = generateMonthsFrom(reportEffectiveStart, reportCols);

  // Report uses dept-filtered users (dept filter applies to report too)
  const reportUsers = users.filter(u =>
    deptFilter.length === 0 || deptFilter.includes(u.departmentId)
  );

  const reportData = reportMonths.map(m => {
    let capacity = 0, booked = 0, uncertain = 0;
    reportUsers.forEach(u => {
      const cell = planData[u.id]?.[m];
      if (!cell) return;
      capacity += cell.capacity;
      cell.entries.forEach(e => {
        if (e.status === 'aktiv') booked += e.hours;
        else if (e.status === 'offertfas') uncertain += e.hours;
      });
    });
    return { month: fmtMonth(m), capacity, booked, uncertain };
  });

  // Department breakdown for report period
  const deptReportData = departments.map(dept => {
    const deptUsers = users.filter(u => u.departmentId === dept.id);
    let capacity = 0, booked = 0, uncertain = 0;
    reportMonths.forEach(m => {
      deptUsers.forEach(u => {
        const cell = planData[u.id]?.[m];
        if (!cell) return;
        capacity += cell.capacity;
        cell.entries.forEach(e => {
          if (e.status === 'aktiv') booked += e.hours;
          else if (e.status === 'offertfas') uncertain += e.hours;
        });
      });
    });
    return { dept, capacity, booked, uncertain, count: deptUsers.length };
  }).filter(d => d.count > 0);

  // Project breakdown for report period (use reportUsers for filtering)
  const projectReportData = activeProjects.map(project => {
    let booked = 0, uncertain = 0;
    reportMonths.forEach(m => {
      reportUsers.forEach(u => {
        const cell = planData[u.id]?.[m];
        if (!cell) return;
        cell.entries.forEach(e => {
          if (e.projectId !== project.id) return;
          if (e.status === 'aktiv') booked += e.hours;
          else if (e.status === 'offertfas') uncertain += e.hours;
        });
      });
    });
    return { project, booked, uncertain };
  }).filter(d => d.booked + d.uncertain > 0)
    .sort((a, b) => (b.booked + b.uncertain) - (a.booked + a.uncertain));

  // ── Dialog helpers ─────────────────────────────────────────────────────────
  function openPlanDialog(userId, month, preProjectId, preRangeEnd = null) {
    setPlanDialog({ userId, month });
    const rangeMonth = month || (months ? months[0] : allMonthsWindow[0]);
    setPlanForm({
      projectId: preProjectId || '',
      hoursPerMonth: '',
      rangeStart: rangeMonth,
      rangeEnd: preRangeEnd || rangeMonth,
      note: '',
    });
  }

  function toggleCollapse(projectId) {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(projectId) ? next.delete(projectId) : next.add(projectId);
      return next;
    });
  }

  // ── Handlers ──────────────────────────────────────────────────────────────
  async function handleSavePlan() {
    if (!planForm.projectId || !planForm.hoursPerMonth || !planDialog) return;
    const evaledVal = evalFormula(planForm.hoursPerMonth);
    if (!evaledVal || evaledVal <= 0) return;
    setSaving(true);
    const project = projects.find(p => p.id === planForm.projectId);
    if (!project) { setSaving(false); return; }
    const { userId } = planDialog;
    const range = generateMonths(planForm.rangeStart, planForm.rangeEnd);
    const newEntries = range.map(m => ({
      id: uid(), userId, month: m,
      hours: hrsPerMonthFromUnit(evaledVal, planUnit, m),
      note: planForm.note,
      createdAt: new Date().toISOString(),
    }));
    const updatedRoughPlan = [...(project.roughPlan || []), ...newEntries];
    await updateDoc(doc(db, 'projects', project.id), { roughPlan: updatedRoughPlan, updatedAt: new Date() });
    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, roughPlan: updatedRoughPlan } : p));
    setSaving(false);
    setPlanDialog(null);
  }

  async function handleSaveFiktiv(overrideInput) {
    if (!fiktivDialog) return;
    setFiktivSaving(true);
    const project = projects.find(p => p.id === fiktivDialog.projectId);
    if (!project) { setFiktivSaving(false); return; }
    const rawVal = parseFloat(overrideInput !== undefined ? overrideInput : fiktivInput) || 0;
    const months = fiktivDialog.months;
    const valPerMonth = fiktivRangeMode === 'total' && months.length > 1
      ? rawVal / months.length
      : rawVal;
    const updated = { ...(project.fiktivPlan || {}) };
    months.forEach(m => {
      if (valPerMonth > 0) updated[m] = valPerMonth;
      else delete updated[m];
    });
    await updateDoc(doc(db, 'projects', project.id), { fiktivPlan: updated, updatedAt: new Date() });
    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, fiktivPlan: updated } : p));
    setFiktivSaving(false);
    setFiktivDialog(null);
    setFiktivDragSel(null);
  }

  function openFiktivDialog(projectId, months) {
    const project = projects.find(p => p.id === projectId);
    const existing = months.length === 1
      ? (parseFloat(project?.fiktivPlan?.[months[0]]) || 0)
      : 0;
    setFiktivDialog({ projectId, months });
    setFiktivInput(existing > 0 ? String(existing) : '');
    setFiktivRangeMode('per');
  }

  async function handleDeleteRoughEntry(projectId, roughId) {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    const updatedRoughPlan = (project.roughPlan || []).filter(e => e.id !== roughId);
    await updateDoc(doc(db, 'projects', project.id), { roughPlan: updatedRoughPlan, updatedAt: new Date() });
    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, roughPlan: updatedRoughPlan } : p));
  }

  function handleFillUp() {
    if (!planDialog) return;
    const cell = planData[planDialog.userId]?.[planDialog.month] || { capacity: 160, total: 0 };
    const remainingMonthly = Math.max(0, cell.capacity - cell.total);
    const wd = workdaysInMonth(planDialog.month);
    let val;
    if (planUnit === 'week')     val = remainingMonthly / (wd / 5);
    else if (planUnit === 'day') val = remainingMonthly / wd;
    else                         val = remainingMonthly;
    setPlanForm(f => ({ ...f, hoursPerMonth: val % 1 === 0 ? val.toFixed(0) : val.toFixed(1) }));
  }

  // ── Column label helpers ───────────────────────────────────────────────────
  function colLabel(col) {
    if (granularity === 'month') return fmtMonth(col);
    return col.label; // week: "v17", day: "mån 14"
  }
  function colSubLabel(col) {
    if (granularity === 'month') {
      const cap = workdaysInMonth(col) * HOURS_PER_DAY;
      return `${workdaysInMonth(col)}d / ${cap}h`;
    }
    if (granularity === 'week') return `${col.monthLabel} · ${weekCapacity(col)}h`;
    // day: show month label only on first day of each month
    return col.isFirstOfMonth ? col.monthLabel : '';
  }
  function colId(col) {
    return granularity === 'month' ? col : col.id;
  }

  // Get hours for a user+col (handles month/week/day)
  function getUserCellHrs(userId, col) {
    if (granularity === 'month') return planData[userId]?.[col]?.total || 0;
    const monthHrs = {};
    allMonthsWindow.forEach(m => { monthHrs[m] = planData[userId]?.[m]?.total || 0; });
    if (granularity === 'week') return weekHrsFromMonthData(monthHrs, col);
    return dayHrsFromMonthData(monthHrs, col);
  }

  function getUserColCapacity(userId, col) {
    if (granularity === 'month') return planData[userId]?.[col]?.capacity || workdaysInMonth(col) * HOURS_PER_DAY;
    if (granularity === 'week') return weekCapacity(col);
    return HOURS_PER_DAY; // 8h per workday
  }

  // Get display entries for a user+col cell (month mode only)
  function getUserCellEntries(userId, col) {
    if (granularity !== 'month') return [];
    return planData[userId]?.[col]?.entries || [];
  }

  // Month for dialog when clicking a week cell: majority month in week
  function weekToMonth(week) {
    const daysPerMonth = {};
    const d = new Date(week.start);
    while (d <= week.end) {
      if (d.getDay() !== 0 && d.getDay() !== 6) {
        const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        daysPerMonth[m] = (daysPerMonth[m] || 0) + 1;
      }
      d.setDate(d.getDate() + 1);
    }
    return Object.entries(daysPerMonth).sort((a, b) => b[1] - a[1])[0]?.[0] || allMonthsWindow[0];
  }

  // Resolve a column to a month string (for the plan dialog)
  function colToMonth(col) {
    if (granularity === 'month') return col;
    if (granularity === 'week') return weekToMonth(col);
    return col.isoMonth;
  }

  const cols = granularity === 'month' ? months : granularity === 'week' ? weeks : days;

  // ── Period label ───────────────────────────────────────────────────────────
  const periodLabel = granularity === 'month'
    ? `${fmtMonth(months[0])} – ${fmtMonth(months[months.length - 1])}`
    : granularity === 'week'
    ? `v${getISOWeek(weeks[0].start)} – v${getISOWeek(weeks[weeks.length - 1].start)}`
    : `${days[0].date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })} – ${days[days.length - 1].date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: '2-digit' })}`;

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  const selectedProject = activeProjects.find(p => p.id === planForm.projectId);
  const rangeMonths = generateMonths(planForm.rangeStart, planForm.rangeEnd);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f4f8' }}>
      {/* ── Top bar ── */}
      <Box sx={{ bgcolor: TEAL_DARK, color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Framtid<span style={{ color: '#4FC3F7' }}>N</span>
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Resursplanering & beläggning
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {viewMode === 'plan' && (
            <>
              <ToggleButtonGroup size="small" exclusive value={viewDimension}
                onChange={(_, v) => v && setViewDimension(v)}
                sx={{ '& .MuiToggleButton-root': {
                  color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)',
                  py: 0.4, px: 1.5, fontSize: '0.72rem',
                  '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.18)', color: 'white' },
                } }}>
                <ToggleButton value="person">Per person</ToggleButton>
                <ToggleButton value="project">Per uppdrag</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup size="small" exclusive value={granularity}
                onChange={(_, v) => { if (v) { setGranularity(v); setWindowStart(null); } }}
                sx={{ '& .MuiToggleButton-root': {
                  color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)',
                  py: 0.4, px: 1.5, fontSize: '0.72rem',
                  '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.18)', color: 'white' },
                } }}>
                <ToggleButton value="month">Månader</ToggleButton>
                <ToggleButton value="week">Veckor</ToggleButton>
                <ToggleButton value="day">Dagar</ToggleButton>
              </ToggleButtonGroup>
              {/* Column count control */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1, px: 0.75, py: 0.3 }}>
                <IconButton size="small" onClick={() => setColsForGranularity(COLS - 1)}
                  sx={{ p: 0.3, color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1, fontWeight: 700 }}>−</Typography>
                </IconButton>
                <Typography sx={{ fontSize: '0.72rem', color: 'white', minWidth: 48, textAlign: 'center' }}>
                  {COLS} {granularity === 'month' ? 'mån' : granularity === 'week' ? 'v' : 'd'}
                </Typography>
                <IconButton size="small" onClick={() => setColsForGranularity(COLS + 1)}
                  sx={{ p: 0.3, color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1, fontWeight: 700 }}>+</Typography>
                </IconButton>
              </Box>
            </>
          )}
          <ToggleButtonGroup size="small" exclusive value={viewMode}
            onChange={(_, v) => v && setViewMode(v)}
            sx={{ '& .MuiToggleButton-root': {
              color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)',
              py: 0.4, px: 1.5, fontSize: '0.72rem',
              '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.18)', color: 'white' },
            } }}>
            <ToggleButton value="plan">Planering</ToggleButton>
            <ToggleButton value="report">Rapport</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Container maxWidth={false} sx={{ py: 2, px: { xs: 1, md: 2 } }}>
        {/* ── Filters + navigation bar ── */}
        {viewMode === 'plan' && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Department filter */}
            <Chip label={`Alla (${users.length})`} size="small"
              onClick={() => setDeptFilter([])}
              sx={{ cursor: 'pointer',
                bgcolor: deptFilter.length === 0 ? TEAL_DARK : 'transparent',
                color: deptFilter.length === 0 ? 'white' : 'text.secondary',
                border: `1px solid ${deptFilter.length === 0 ? TEAL_DARK : '#e0e0e0'}` }} />
            {departments.map(d => {
              const sel = deptFilter.includes(d.id);
              const count = users.filter(u => u.departmentId === d.id).length;
              if (!count) return null;
              return (
                <Chip key={d.id} label={`${deptCode(d)} (${count})`} size="small" title={d.name}
                  onClick={() => setDeptFilter(prev => sel ? prev.filter(id => id !== d.id) : [...prev, d.id])}
                  sx={{ cursor: 'pointer',
                    bgcolor: sel ? TEAL_DARK : 'transparent',
                    color: sel ? 'white' : 'text.secondary',
                    border: `1px solid ${sel ? TEAL_DARK : '#e0e0e0'}` }} />
              );
            })}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            {/* Project search */}
            <TextField size="small" placeholder="Filtrera uppdrag…"
              value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
              sx={{ width: 200, '& input': { fontSize: '0.8rem', py: '5px' } }} />
            {/* Hide empty */}
            <Tooltip title={hideEmpty ? 'Visa alla rader' : 'Dölj rader utan data'}>
              <IconButton size="small" onClick={() => setHideEmpty(v => !v)}
                sx={{ color: hideEmpty ? TEAL : 'action.disabled',
                  border: `1px solid ${hideEmpty ? TEAL : '#e0e0e0'}`, borderRadius: 1, p: 0.5 }}>
                <VisibilityOffIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            {/* Time window picker */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'white',
              border: '1px solid #e0e0e0', borderRadius: 1, px: 0.75, py: 0.4 }}>
              <IconButton size="small"
                onClick={() => setWindowStart(shiftWindowStart(effectiveStart, granularity, -COLS))}
                sx={{ p: 0.4 }}>
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
              {/* Start date picker */}
              <input
                type={granularity === 'month' ? 'month' : granularity === 'week' ? 'week' : 'date'}
                value={effectiveStart}
                onChange={e => setWindowStart(e.target.value || null)}
                style={{
                  border: 'none', outline: 'none', fontSize: '0.78rem',
                  color: TEAL_DARK, fontWeight: 600, background: 'transparent',
                  width: granularity === 'month' ? 110 : granularity === 'week' ? 130 : 120,
                  cursor: 'pointer',
                }}
              />
              <Typography sx={{ fontSize: '0.72rem', color: '#999', mx: 0.3 }}>→</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: TEAL_DARK, fontWeight: 600 }}>
                {periodLabel.split('–')[1]?.trim() || ''}
              </Typography>
              <IconButton size="small"
                onClick={() => setWindowStart(shiftWindowStart(effectiveStart, granularity, COLS))}
                sx={{ p: 0.4 }}>
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
              {windowStart !== null && (
                <Button size="small" onClick={() => setWindowStart(null)}
                  sx={{ fontSize: '0.65rem', py: 0.2, px: 0.8, minWidth: 0, color: TEAL }}>
                  Nu
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Filters for report */}
        {viewMode === 'report' && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Department filter */}
            <Chip label={`Alla (${users.length})`} size="small"
              onClick={() => setDeptFilter([])}
              sx={{ cursor: 'pointer',
                bgcolor: deptFilter.length === 0 ? TEAL_DARK : 'transparent',
                color: deptFilter.length === 0 ? 'white' : 'text.secondary',
                border: `1px solid ${deptFilter.length === 0 ? TEAL_DARK : '#e0e0e0'}` }} />
            {departments.map(d => {
              const sel = deptFilter.includes(d.id);
              const count = users.filter(u => u.departmentId === d.id).length;
              if (!count) return null;
              return (
                <Chip key={d.id} label={`${deptCode(d)} (${count})`} size="small" title={d.name}
                  onClick={() => setDeptFilter(prev => sel ? prev.filter(id => id !== d.id) : [...prev, d.id])}
                  sx={{ cursor: 'pointer',
                    bgcolor: sel ? TEAL_DARK : 'transparent',
                    color: sel ? 'white' : 'text.secondary',
                    border: `1px solid ${sel ? TEAL_DARK : '#e0e0e0'}` }} />
              );
            })}
            <Box sx={{ flex: 1 }} />
            {/* Time window picker for report */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'white',
              border: '1px solid #e0e0e0', borderRadius: 1, px: 0.75, py: 0.4 }}>
              <IconButton size="small"
                onClick={() => setReportStart(shiftWindowStart(reportEffectiveStart, 'month', -reportCols))}
                sx={{ p: 0.4 }}>
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <input
                type="month"
                value={reportEffectiveStart}
                onChange={e => setReportStart(e.target.value || null)}
                style={{
                  border: 'none', outline: 'none', fontSize: '0.78rem',
                  color: TEAL_DARK, fontWeight: 600, background: 'transparent',
                  width: 110, cursor: 'pointer',
                }}
              />
              <Typography sx={{ fontSize: '0.72rem', color: '#999', mx: 0.3 }}>→</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: TEAL_DARK, fontWeight: 600 }}>
                {fmtMonth(reportMonths[reportMonths.length - 1])}
              </Typography>
              <IconButton size="small"
                onClick={() => setReportStart(shiftWindowStart(reportEffectiveStart, 'month', reportCols))}
                sx={{ p: 0.4 }}>
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
              {reportStart !== null && (
                <Button size="small" onClick={() => setReportStart(null)}
                  sx={{ fontSize: '0.65rem', py: 0.2, px: 0.8, minWidth: 0, color: TEAL }}>
                  Nu
                </Button>
              )}
            </Box>
            {/* Column count for report */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
              bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 1, px: 0.75, py: 0.3 }}>
              <IconButton size="small" onClick={() => setReportCols(reportCols - 1)}
                sx={{ p: 0.3, color: 'text.secondary', '&:hover': { color: TEAL_DARK } }}>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1, fontWeight: 700 }}>−</Typography>
              </IconButton>
              <Typography sx={{ fontSize: '0.72rem', color: TEAL_DARK, minWidth: 44, textAlign: 'center', fontWeight: 600 }}>
                {reportCols} mån
              </Typography>
              <IconButton size="small" onClick={() => setReportCols(reportCols + 1)}
                sx={{ p: 0.3, color: 'text.secondary', '&:hover': { color: TEAL_DARK } }}>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1, fontWeight: 700 }}>+</Typography>
              </IconButton>
            </Box>
          </Box>
        )}

        {/* ═══════════════ PLAN — PERSON VIEW ═══════════════ */}
        {viewMode === 'plan' && viewDimension === 'person' && (
          <Paper sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Box sx={{ overflowX: 'auto', maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      color: 'white', fontWeight: 700, fontSize: '0.75rem',
                      position: 'sticky', left: 0, top: 0, zIndex: 5,
                      bgcolor: `${TEAL_DARK} !important`,
                      width: 170, minWidth: 170, borderRight: '1px solid rgba(255,255,255,0.15)',
                    }}>Resurs</TableCell>
                    {cols.map(col => (
                      <TableCell key={colId(col)} align="center"
                        sx={{ color: 'white', fontWeight: 600, fontSize: '0.7rem', p: '6px 3px',
                          whiteSpace: 'nowrap', minWidth: granularity === 'day' ? 52 : 78,
                          bgcolor: `${TEAL_DARK} !important`, top: 0, zIndex: 2,
                          ...(granularity === 'day' && col.isFirstOfMonth && { borderLeft: '2px solid rgba(255,255,255,0.35)' }),
                        }}>
                        {colLabel(col)}
                        <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.2, display: 'block' }}>
                          {colSubLabel(col)}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleUsers.map(user => {
                    const dept = departments.find(d => d.id === user.departmentId);
                    return (
                      <TableRow key={user.id}
                        sx={{ '&:nth-of-type(even) td': { bgcolor: '#fafcfe' }, '& td': { verticalAlign: 'top' } }}>
                        <TableCell sx={{
                          position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper',
                          width: 170, minWidth: 170, py: 1, borderRight: '2px solid #dce8f5',
                          '.MuiTableRow-root:nth-of-type(even) &': { bgcolor: '#fafcfe' },
                        }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.3 }}>
                            {user.displayName || user.name || user.email}
                          </Typography>
                          {dept && (
                            <Chip label={deptCode(dept)} size="small" title={dept.name}
                              sx={{ fontSize: '0.58rem', height: 14, mt: 0.3,
                                bgcolor: '#DCEEFB', color: TEAL_DARK, borderRadius: 0.5 }} />
                          )}
                        </TableCell>

                        {cols.map((col, colIdx) => {
                          const h = getUserCellHrs(user.id, col);
                          const cap = getUserColCapacity(user.id, col);
                          const pct = cap > 0 ? (h / cap) * 100 : 0;
                          const colors = utilizationColors(Math.round(pct));
                          const entries = getUserCellEntries(user.id, col);
                          const clickMonth = colToMonth(col);
                          const isSelected = dragSel
                            && dragSel.userId === user.id
                            && colIdx >= Math.min(dragSel.startIdx, dragSel.endIdx)
                            && colIdx <= Math.max(dragSel.startIdx, dragSel.endIdx);
                          return (
                            <TableCell key={colId(col)} align="center"
                              sx={{
                                p: granularity === 'day' ? '4px 2px' : '5px 4px',
                                minWidth: granularity === 'day' ? 52 : 78, verticalAlign: 'top',
                                bgcolor: isSelected ? '#DBEAFE' : h > 0 ? colors.bg : undefined,
                                outline: isSelected ? `2px solid ${TEAL}` : 'none',
                                outlineOffset: '-1px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'background-color 0.05s',
                                '&:hover': { filter: isSelected ? 'none' : 'brightness(0.93)' },
                                ...(granularity === 'day' && col.isFirstOfMonth && { borderLeft: '2px solid #c5d8ef' }),
                              }}
                              onMouseDown={e => {
                                e.preventDefault();
                                setDragSel({ userId: user.id, startIdx: colIdx, endIdx: colIdx, active: true });
                              }}
                              onMouseEnter={() => {
                                if (dragSel?.active && dragSel.userId === user.id) {
                                  setDragSel(s => ({ ...s, endIdx: colIdx }));
                                }
                              }}
                              onMouseUp={() => {
                                if (!dragSel?.active) return;
                                const isSingleClick = dragSel.startIdx === dragSel.endIdx;
                                setDragSel(s => ({ ...s, active: false }));
                                if (isSingleClick) {
                                  setDragSel(null);
                                  openPlanDialog(user.id, clickMonth);
                                }
                                // multi-cell: keep selection, show floating bar
                              }}>
                              {/* Month mode: per-project labels */}
                              {entries.map((e, ei) => {
                                const sc = STATUS_META[e.status] || STATUS_META.offertfas;
                                return (
                                  <Tooltip key={ei} title={
                                    `${e.projectName}${e.projectNumber ? ` · ${e.projectNumber}` : ''} — ${e.hours.toFixed(1)} tim${e.type === 'rough' ? ' (grovplan)' : ''}`
                                  } placement="top">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.2, overflow: 'hidden' }}
                                      onClick={ev => { ev.stopPropagation(); navigate(`/projects/${e.projectId}`); }}>
                                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                        bgcolor: sc.color, opacity: e.type === 'rough' ? 0.5 : 1 }} />
                                      <Typography sx={{
                                        fontSize: '0.62rem', lineHeight: 1.25,
                                        fontWeight: e.type === 'budget' ? 700 : 400,
                                        fontStyle: e.type === 'rough' ? 'italic' : 'normal',
                                        color: sc.color, overflow: 'hidden', textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap', maxWidth: 65,
                                      }}>
                                        {e.hours.toFixed(0)}h {e.projectNumber || e.projectName.slice(0, 7)}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                              {/* Week / day mode: aggregate */}
                              {granularity !== 'month' && h > 0 && (
                                <Typography sx={{ fontSize: granularity === 'day' ? '0.68rem' : '0.65rem', fontWeight: 700, color: colors.text, lineHeight: 1.3, textAlign: 'center' }}>
                                  {h.toFixed(granularity === 'day' ? 1 : 0)}h
                                </Typography>
                              )}
                              {h > 0 ? (
                                <Box sx={{ mt: 0.3 }}>
                                  <Box sx={{ height: granularity === 'day' ? 4 : 3, bgcolor: 'rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', borderRadius: 2, bgcolor: colors.bar,
                                      width: `${Math.min(pct, 100)}%`, transition: 'width 0.3s' }} />
                                  </Box>
                                  {granularity !== 'day' && (
                                    <Typography sx={{ fontSize: '0.58rem', color: colors.text, fontWeight: 700, mt: 0.2, lineHeight: 1 }}>
                                      {h.toFixed(0)}/{cap}h ({Math.round(pct)}%)
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Box sx={{ opacity: 0, '&:hover': { opacity: 1 }, color: '#ccc',
                                  display: 'flex', justifyContent: 'center' }}>
                                  <AddIcon sx={{ fontSize: 14 }} />
                                </Box>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}

                  {/* Totals row */}
                  <TableRow>
                    <TableCell sx={{
                      position: 'sticky', left: 0, zIndex: 1, bgcolor: TEAL_DARK,
                      width: 170, minWidth: 170, color: 'white', fontWeight: 700,
                      fontSize: '0.8rem', borderRight: '2px solid #dce8f5',
                    }}>Totalt team</TableCell>
                    {cols.map(col => {
                      const cap = visibleUsers.reduce((s, u) => s + getUserColCapacity(u.id, col), 0);
                      const tot = visibleUsers.reduce((s, u) => s + getUserCellHrs(u.id, col), 0);
                      const pct = cap > 0 ? (tot / cap) * 100 : 0;
                      const colors = utilizationColors(Math.round(pct));
                      return (
                        <TableCell key={colId(col)} align="center" sx={{ bgcolor: TEAL_DARK, p: '5px 3px' }}>
                          <Box sx={{ height: 4, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, overflow: 'hidden', mx: 0.5 }}>
                            <Box sx={{ height: '100%', width: `${Math.min(pct, 100)}%`, bgcolor: colors.bar, borderRadius: 2 }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.65rem', color: tot > 0 ? colors.bar : 'rgba(255,255,255,0.5)', fontWeight: 700, mt: 0.2 }}>
                            {Math.round(pct)}%
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.6)' }}>
                            {tot.toFixed(0)}h
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
            <Box sx={{ px: 2, py: 1, bgcolor: '#f8fafc', borderTop: '1px solid #e0e0e0',
              display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { bg: '#E8F5E9', bar: '#4CAF50', label: '< 50%' },
                { bg: '#E3F2FD', bar: '#1976D2', label: '50–80%' },
                { bg: '#FFF3E0', bar: '#F57C00', label: '80–100%' },
                { bg: '#FFEBEE', bar: '#D32F2F', label: '> 100%' },
              ].map(({ bg, bar, label }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <Box sx={{ width: 11, height: 11, borderRadius: 0.4, bgcolor: bg, border: `1.5px solid ${bar}` }} />
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Box>
              ))}
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                Klicka på uppdrag i cell → öppnar uppdraget · Klicka cell → Planera-dialog
              </Typography>
            </Box>
          </Paper>
        )}

        {/* ═══════════════ PLAN — PROJECT VIEW ═══════════════ */}
        {viewMode === 'plan' && viewDimension === 'project' && (
          <Paper sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Box sx={{ overflowX: 'auto', maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      color: 'white', fontWeight: 700, fontSize: '0.75rem',
                      position: 'sticky', left: 0, top: 0, zIndex: 5,
                      bgcolor: `${TEAL_DARK} !important`,
                      width: 220, minWidth: 220, borderRight: '1px solid rgba(255,255,255,0.15)',
                    }}>Uppdrag / Resurs</TableCell>
                    {cols.map(col => (
                      <TableCell key={colId(col)} align="center"
                        sx={{ color: 'white', fontWeight: 600, fontSize: '0.7rem', p: '6px 3px',
                          whiteSpace: 'nowrap', minWidth: granularity === 'day' ? 52 : 78, bgcolor: `${TEAL_DARK} !important`, zIndex: 2 }}>
                        {colLabel(col)}
                        <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.2, display: 'block' }}>
                          {colSubLabel(col)}
                        </Typography>
                      </TableCell>
                    ))}
                    <TableCell align="right"
                      sx={{ color: 'white', fontWeight: 600, fontSize: '0.7rem',
                        whiteSpace: 'nowrap', minWidth: 80, bgcolor: `${TEAL_DARK} !important`, zIndex: 2 }}>
                      Totalt
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleProjectRows.map(({ project, resRows, roughOnlyRows, totalMonthHrs, roughMonthHrs, totalRoughHrs, fiktivMonthHrs, totalFiktivHrs, totalBudgetHrs, totalBudgetVal }) => {
                    const sc = STATUS_META[project.status] || STATUS_META.offertfas;
                    const isCollapsed = collapsed.has(project.id);

                    const getColHrs = (col, mhrs) => {
                      if (granularity === 'month') return mhrs[col] || 0;
                      if (granularity === 'week') return weekHrsFromMonthData(mhrs, col);
                      return dayHrsFromMonthData(mhrs, col);
                    };

                    return (
                      <React.Fragment key={project.id}>
                        <TableRow sx={{ bgcolor: '#EFF6FF', '&:hover td': { bgcolor: '#DBEAFE' } }}>
                          <TableCell sx={{
                            position: 'sticky', left: 0, zIndex: 1, bgcolor: '#EFF6FF',
                            width: 220, minWidth: 220, py: 1, borderRight: '2px solid #BFDBFE',
                            '.MuiTableRow-root:hover &': { bgcolor: '#DBEAFE' },
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton size="small" onClick={() => toggleCollapse(project.id)}
                                sx={{ p: 0.2, color: TEAL_DARK }}>
                                {isCollapsed ? <KeyboardArrowRightIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEAL_DARK, lineHeight: 1.3, cursor: 'pointer' }}
                                    onClick={() => navigate(`/projects/${project.id}`)}>
                                    {project.name || '(namnlöst)'}
                                  </Typography>
                                  <Chip label={sc.label} size="small"
                                    sx={{ fontSize: '0.58rem', height: 15, bgcolor: sc.bg, color: sc.color }} />
                                </Box>
                                {project.projectNumber && (
                                  <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>{project.projectNumber}</Typography>
                                )}
                              </Box>
                              <IconButton size="small" onClick={() => navigate(`/projects/${project.id}`)}
                                sx={{ p: 0.3, color: 'text.disabled', flexShrink: 0 }}>
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                          {cols.map(col => {
                            const budgH  = getColHrs(col, totalMonthHrs);
                            const roughH = getColHrs(col, roughMonthHrs);
                            const fiktH  = getColHrs(col, fiktivMonthHrs);
                            const total  = budgH + roughH + fiktH;
                            return (
                              <TableCell key={colId(col)} align="center" sx={{ p: '5px 3px' }}>
                                {total > 0.05 ? (
                                  <Box>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: TEAL_DARK, lineHeight: 1.3 }}>
                                      {total.toFixed(0)}h
                                    </Typography>
                                    {(roughH > 0.05 || fiktH > 0.05) && (
                                      <Typography sx={{ fontSize: '0.58rem', color: '#6B7280', lineHeight: 1.2 }}>
                                        {[
                                          budgH > 0.05 ? `${budgH.toFixed(0)}b` : null,
                                          roughH > 0.05 ? `${roughH.toFixed(0)}g` : null,
                                          fiktH > 0.05 ? `${fiktH.toFixed(0)}ej` : null,
                                        ].filter(Boolean).join('+')}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography sx={{ fontSize: '0.65rem', color: '#ddd' }}>–</Typography>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell align="right" sx={{ p: '5px 8px' }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700,
                              color: (totalBudgetHrs + totalRoughHrs + totalFiktivHrs) > 0 ? TEAL_DARK : 'text.disabled' }}>
                              {(totalBudgetHrs + totalRoughHrs + totalFiktivHrs) > 0 ? `${(totalBudgetHrs + totalRoughHrs + totalFiktivHrs).toFixed(0)}h` : '–'}
                            </Typography>
                            {totalBudgetVal > 0 && (
                              <Typography sx={{ fontSize: '0.62rem', color: '#2e7d32' }}>{fmtSEK(totalBudgetVal)}</Typography>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Budget summary row */}
                        {!isCollapsed && resRows.length > 0 && (
                          <TableRow sx={{ '& td': { bgcolor: '#D1D5DB', borderBottom: '1px solid #9CA3AF' } }}>
                            <TableCell sx={{
                              position: 'sticky', left: 0, zIndex: 1, bgcolor: '#D1D5DB',
                              width: 220, minWidth: 220, pl: 4.5, py: 0.5,
                              borderRight: '2px solid #9CA3AF',
                            }}>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#374151',
                                fontStyle: 'italic', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                                Budget
                              </Typography>
                            </TableCell>
                            {cols.map(col => {
                              const h = getColHrs(col, totalMonthHrs);
                              return (
                                <TableCell key={colId(col)} align="center" sx={{ p: '4px 3px' }}>
                                  {h > 0.05 ? (
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700,
                                      fontStyle: 'italic', color: '#374151' }}>
                                      {h.toFixed(0)}h
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF' }}>–</Typography>
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell align="right" sx={{ p: '4px 8px' }}>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700,
                                fontStyle: 'italic', color: '#374151' }}>
                                {totalBudgetHrs > 0 ? `${totalBudgetHrs.toFixed(0)}h` : '–'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Ej tillsatt row */}
                        {!isCollapsed && (
                          <TableRow sx={{ '& td': { bgcolor: '#FFF9EC', borderBottom: '1px solid #FDE68A' } }}>
                            <TableCell sx={{
                              position: 'sticky', left: 0, zIndex: 1, bgcolor: '#FFF9EC',
                              width: 220, minWidth: 220, pl: 4.5, py: 0.5,
                              borderRight: '2px solid #FDE68A',
                            }}>
                              <Typography sx={{ fontSize: '0.72rem', fontStyle: 'italic', color: '#92400E' }}>
                                Ej tillsatt
                              </Typography>
                            </TableCell>
                            {cols.map((col, colIdx) => {
                              const month = colToMonth(col);
                              const h = parseFloat(project.fiktivPlan?.[month]) || 0;
                              const isFiktivSelected = fiktivDragSel
                                && fiktivDragSel.projectId === project.id
                                && colIdx >= Math.min(fiktivDragSel.startIdx, fiktivDragSel.endIdx)
                                && colIdx <= Math.max(fiktivDragSel.startIdx, fiktivDragSel.endIdx);
                              return (
                                <TableCell key={colId(col)} align="center"
                                  sx={{
                                    p: '4px 3px', cursor: 'pointer', userSelect: 'none',
                                    bgcolor: isFiktivSelected ? '#FEF3C7' : undefined,
                                    outline: isFiktivSelected ? '2px solid #D97706' : 'none',
                                    outlineOffset: '-1px',
                                    '&:hover': { bgcolor: isFiktivSelected ? '#FEF3C7' : '#FFF3DC' },
                                  }}
                                  onMouseDown={e => {
                                    e.preventDefault();
                                    setFiktivDragSel({ projectId: project.id, startIdx: colIdx, endIdx: colIdx, active: true });
                                  }}
                                  onMouseEnter={() => {
                                    if (fiktivDragSel?.active && fiktivDragSel.projectId === project.id) {
                                      setFiktivDragSel(s => ({ ...s, endIdx: colIdx }));
                                    }
                                  }}
                                  onMouseUp={() => {
                                    if (!fiktivDragSel?.active) return;
                                    const lo = Math.min(fiktivDragSel.startIdx, fiktivDragSel.endIdx);
                                    const hi = Math.max(fiktivDragSel.startIdx, fiktivDragSel.endIdx);
                                    const isSingleClick = lo === hi;
                                    setFiktivDragSel(s => ({ ...s, active: false }));
                                    if (isSingleClick) {
                                      setFiktivDragSel(null);
                                      openFiktivDialog(project.id, [month]);
                                    } else {
                                      // range: collect unique months across selected cols
                                      const selMonths = [...new Set(
                                        cols.slice(lo, hi + 1).map(c => colToMonth(c))
                                      )];
                                      openFiktivDialog(project.id, selMonths);
                                    }
                                  }}>
                                  {h > 0.05 ? (
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600,
                                      fontStyle: 'italic', color: '#92400E' }}>
                                      {h.toFixed(0)}h
                                    </Typography>
                                  ) : (
                                    <AddIcon sx={{ fontSize: 11, color: '#D97706', opacity: 0,
                                      '.MuiTableCell-root:hover &': { opacity: 1 } }} />
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell align="right" sx={{ p: '4px 8px' }}>
                              {totalFiktivHrs > 0 && (
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600,
                                  fontStyle: 'italic', color: '#92400E' }}>
                                  {totalFiktivHrs.toFixed(0)}h
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        )}

                        {!isCollapsed && (
                          <>
                            {resRows.length === 0 ? (
                              <TableRow sx={{ '& td': { bgcolor: '#fafcfe', borderBottom: '1px solid #f0f0f0' } }}>
                                <TableCell sx={{
                                  position: 'sticky', left: 0, zIndex: 1, bgcolor: '#fafcfe',
                                  width: 220, minWidth: 220, pl: 5, borderRight: '2px solid #dce8f5',
                                }}>
                                  <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontStyle: 'italic' }}>
                                    Ingen budget skapad
                                  </Typography>
                                </TableCell>
                                {cols.map(col => (
                                  <TableCell key={colId(col)} align="center" sx={{ p: '4px 3px',
                                    cursor: 'pointer', '&:hover': { bgcolor: '#f0f4ff' } }}
                                    onClick={() => openPlanDialog(null, colToMonth(col), project.id)}>
                                    <AddIcon sx={{ fontSize: 12, color: '#ccc', opacity: 0,
                                      '.MuiTableCell-root:hover &': { opacity: 1 } }} />
                                  </TableCell>
                                ))}
                                <TableCell />
                              </TableRow>
                            ) : (
                              resRows.map(({ res, user, monthHrs, roughHrs, totalHrs, totalVal }) => (
                                <TableRow key={res.id}
                                  sx={{ '& td': { bgcolor: '#fafcfe', borderBottom: '1px solid #f0f0f0' },
                                    '&:hover td': { bgcolor: '#f0f6ff' } }}>
                                  <TableCell sx={{
                                    position: 'sticky', left: 0, zIndex: 1, bgcolor: '#fafcfe',
                                    width: 220, minWidth: 220, pl: 4.5, py: 0.7,
                                    borderRight: '2px solid #dce8f5',
                                    '.MuiTableRow-root:hover &': { bgcolor: '#f0f6ff' },
                                  }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#333', lineHeight: 1.3 }}>
                                      {user ? (user.displayName || user.name) : res.name}
                                    </Typography>
                                  </TableCell>
                                  {cols.map(col => {
                                    const budgH = getColHrs(col, monthHrs);
                                    const rghH  = getColHrs(col, roughHrs);
                                    const h = budgH + rghH;
                                    const clickMonth = colToMonth(col);
                                    return (
                                      <TableCell key={colId(col)} align="center" sx={{ p: '4px 3px',
                                        cursor: user ? 'pointer' : 'default',
                                        '&:hover': user ? { bgcolor: '#e8f0fe' } : {} }}
                                        onClick={() => user && openPlanDialog(user.id, clickMonth, project.id)}>
                                        {h > 0.05 ? (
                                          <Box>
                                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: TEAL_DARK }}>
                                              {h.toFixed(0)}h
                                            </Typography>
                                            {budgH > 0.05 && rghH > 0.05 && (
                                              <Typography sx={{ fontSize: '0.58rem', color: '#6B7280', lineHeight: 1.2 }}>
                                                {budgH.toFixed(0)}b+{rghH.toFixed(0)}g
                                              </Typography>
                                            )}
                                          </Box>
                                        ) : (
                                          <Typography sx={{ fontSize: '0.65rem', color: '#ddd' }}>–</Typography>
                                        )}
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell align="right" sx={{ p: '4px 8px' }}>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: totalHrs > 0 ? 600 : 400,
                                      color: totalHrs > 0 ? TEAL_DARK : 'text.disabled' }}>
                                      {totalHrs > 0 ? `${totalHrs.toFixed(0)}h` : '–'}
                                    </Typography>
                                    {totalVal > 0 && (
                                      <Typography sx={{ fontSize: '0.6rem', color: '#2e7d32' }}>{fmtSEK(totalVal)}</Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                            {/* Rough-plan-only rows: users with grovplan but no budget resource */}
                            {!isCollapsed && roughOnlyRows.map(({ user, uid, monthHrs: rghMhrs }) => (
                              <TableRow key={`rough-${uid}`}
                                sx={{ '& td': { bgcolor: '#fafcfe', borderBottom: '1px solid #f0f0f0' },
                                  '&:hover td': { bgcolor: '#f0f6ff' } }}>
                                <TableCell sx={{
                                  position: 'sticky', left: 0, zIndex: 1, bgcolor: '#fafcfe',
                                  width: 220, minWidth: 220, pl: 4.5, py: 0.7,
                                  borderRight: '2px solid #dce8f5',
                                  '.MuiTableRow-root:hover &': { bgcolor: '#f0f6ff' },
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#333', lineHeight: 1.3 }}>
                                      {user ? (user.displayName || user.name) : uid}
                                    </Typography>
                                    <Chip label="grovplan" size="small"
                                      sx={{ fontSize: '0.55rem', height: 14, bgcolor: '#EDE7F6', color: '#5E35B1' }} />
                                  </Box>
                                </TableCell>
                                {cols.map(col => {
                                  const h = getColHrs(col, rghMhrs);
                                  const clickMonth = colToMonth(col);
                                  return (
                                    <TableCell key={colId(col)} align="center" sx={{ p: '4px 3px',
                                      cursor: user ? 'pointer' : 'default',
                                      '&:hover': user ? { bgcolor: '#e8f0fe' } : {} }}
                                      onClick={() => user && openPlanDialog(user.id, clickMonth, project.id)}>
                                      {h > 0.05 ? (
                                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600,
                                          color: '#5E35B1', fontStyle: 'italic' }}>
                                          {h.toFixed(0)}h
                                        </Typography>
                                      ) : (
                                        <Typography sx={{ fontSize: '0.65rem', color: '#ddd' }}>–</Typography>
                                      )}
                                    </TableCell>
                                  );
                                })}
                                <TableCell align="right" sx={{ p: '4px 8px' }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#5E35B1', fontStyle: 'italic' }}>
                                    {allMonthsWindow.reduce((s, m) => s + (rghMhrs[m] || 0), 0).toFixed(0)}h
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <Box sx={{ px: 2, py: 1, bgcolor: '#f8fafc', borderTop: '1px solid #e0e0e0',
              display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: TEAL_DARK }}>120h</Typography>
                <Typography variant="caption" color="text.secondary">= budget + grovplan + ej tillsatt (huvudrad) · b/g/ej i detalj</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                <Typography variant="caption" sx={{ fontStyle: 'italic', fontWeight: 700, color: '#374151',
                  bgcolor: '#D1D5DB', px: 0.6, borderRadius: 0.5 }}>100h</Typography>
                <Typography variant="caption" color="text.secondary">= budgetrad</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                <Typography variant="caption" sx={{ fontStyle: 'italic', fontWeight: 600, color: '#92400E',
                  bgcolor: '#FFF9EC', px: 0.6, borderRadius: 0.5 }}>20h</Typography>
                <Typography variant="caption" color="text.secondary">= ej tillsatt (klicka för att redigera)</Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* ═══════════════ REPORT VIEW ═══════════════ */}
        {viewMode === 'report' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h6" fontWeight="bold">
                  Beläggningsgrad
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {reportUsers.length} medarbetare · {fmtMonth(reportMonths[0])} – {fmtMonth(reportMonths[reportMonths.length - 1])}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Streckad linje = tillgänglig kapacitet. Staplat = bokad tid (aktiva) + osäker tid (offertfas).
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={reportData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradBooked" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1976D2" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#1976D2" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="gradUncertain" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F57C00" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F57C00" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8edf2" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} unit="h" width={48} />
                  <ReTooltip formatter={(v, name) => [`${v.toFixed(0)} tim`, name]} contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                  <Area type="monotone" dataKey="capacity" name="Tillgänglig kapacitet"
                    stroke="#90A4AE" fill="none" strokeDasharray="5 4" strokeWidth={2} />
                  <Area type="monotone" dataKey="booked" name="Bokad (aktiva uppdrag)"
                    stroke="#1976D2" fill="url(#gradBooked)" strokeWidth={2} stackId="load" />
                  <Area type="monotone" dataKey="uncertain" name="Osäker (offertfas)"
                    stroke="#F57C00" fill="url(#gradUncertain)" strokeWidth={2} stackId="load" />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
            <Paper sx={{ overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography variant="subtitle1" fontWeight="bold">Månadsöversikt</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.75rem' } }}>
                    <TableCell>Månad</TableCell>
                    <TableCell align="right">Kapacitet</TableCell>
                    <TableCell align="right" sx={{ color: '#1565C0' }}>Bokad (aktiv)</TableCell>
                    <TableCell align="right" sx={{ color: '#E65100' }}>Osäker (offert)</TableCell>
                    <TableCell align="right">Total last</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>Beläggning</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map(row => {
                    const total = row.booked + row.uncertain;
                    const pct = row.capacity > 0 ? Math.round((total / row.capacity) * 100) : 0;
                    const colors = utilizationColors(pct);
                    return (
                      <TableRow key={row.month} sx={{ '&:hover': { bgcolor: '#f5f7fa' } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{row.month}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>{row.capacity.toFixed(0)} tim</TableCell>
                        <TableCell align="right" sx={{ color: '#1565C0', fontWeight: 600 }}>
                          {row.booked > 0 ? `${row.booked.toFixed(0)} tim` : '–'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#E65100' }}>
                          {row.uncertain > 0 ? `${row.uncertain.toFixed(0)} tim` : '–'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {total > 0 ? `${total.toFixed(0)} tim` : '–'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1, height: 8, bgcolor: '#e0e8f0', borderRadius: 4, overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', borderRadius: 4, bgcolor: colors.bar,
                                width: `${Math.min(pct, 100)}%`, transition: 'width 0.3s' }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.78rem', color: colors.text || 'text.secondary',
                              fontWeight: 700, minWidth: 36, textAlign: 'right' }}>
                              {pct}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>

            {/* ── Department breakdown ── */}
            {deptReportData.length > 0 && deptFilter.length === 0 && (
              <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="subtitle1" fontWeight="bold">Per affärsområde</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtMonth(reportMonths[0])} – {fmtMonth(reportMonths[reportMonths.length - 1])} · summerat
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.75rem' } }}>
                      <TableCell>Affärsområde</TableCell>
                      <TableCell align="right">Medarbetare</TableCell>
                      <TableCell align="right">Kapacitet</TableCell>
                      <TableCell align="right" sx={{ color: '#1565C0' }}>Bokad</TableCell>
                      <TableCell align="right" sx={{ color: '#E65100' }}>Osäker</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>Beläggning</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deptReportData.map(({ dept, capacity, booked, uncertain, count }) => {
                      const total = booked + uncertain;
                      const pct = capacity > 0 ? Math.round((total / capacity) * 100) : 0;
                      const colors = utilizationColors(pct);
                      return (
                        <TableRow key={dept.id} sx={{ '&:hover': { bgcolor: '#f5f7fa' } }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={deptCode(dept)} size="small" title={dept.name}
                                sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#DCEEFB', color: TEAL_DARK }} />
                              <Typography sx={{ fontSize: '0.82rem' }}>{dept.name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>{count}</TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>{capacity.toFixed(0)} tim</TableCell>
                          <TableCell align="right" sx={{ color: '#1565C0', fontWeight: 600 }}>
                            {booked > 0 ? `${booked.toFixed(0)} tim` : '–'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#E65100' }}>
                            {uncertain > 0 ? `${uncertain.toFixed(0)} tim` : '–'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1, height: 8, bgcolor: '#e0e8f0', borderRadius: 4, overflow: 'hidden' }}>
                                <Box sx={{ height: '100%', borderRadius: 4, bgcolor: colors.bar,
                                  width: `${Math.min(pct, 100)}%` }} />
                              </Box>
                              <Typography sx={{ fontSize: '0.78rem', color: colors.text || 'text.secondary',
                                fontWeight: 700, minWidth: 36, textAlign: 'right' }}>
                                {pct}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            )}

            {/* ── Project breakdown ── */}
            {projectReportData.length > 0 && (
              <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="subtitle1" fontWeight="bold">Per uppdrag</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtMonth(reportMonths[0])} – {fmtMonth(reportMonths[reportMonths.length - 1])} · planerade timmar
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f7fa', fontSize: '0.75rem' } }}>
                      <TableCell>Uppdrag</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" sx={{ color: '#1565C0' }}>Bokad (aktiv)</TableCell>
                      <TableCell align="right" sx={{ color: '#E65100' }}>Osäker (offert)</TableCell>
                      <TableCell align="right">Totalt</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectReportData.map(({ project, booked, uncertain }) => {
                      const sc = STATUS_META[project.status] || STATUS_META.offertfas;
                      const total = booked + uncertain;
                      return (
                        <TableRow key={project.id} sx={{ '&:hover': { bgcolor: '#f5f7fa', cursor: 'pointer' } }}
                          onClick={() => navigate(`/projects/${project.id}`)}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                {project.name || '(namnlöst)'}
                              </Typography>
                              {project.projectNumber && (
                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                  {project.projectNumber}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={sc.label} size="small"
                              sx={{ fontSize: '0.62rem', height: 17, bgcolor: sc.bg, color: sc.color }} />
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#1565C0', fontWeight: booked > 0 ? 600 : 400 }}>
                            {booked > 0 ? `${booked.toFixed(0)} tim` : '–'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#E65100' }}>
                            {uncertain > 0 ? `${uncertain.toFixed(0)} tim` : '–'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {total.toFixed(0)} tim
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Box>
        )}
      </Container>

      {/* ═══════════════ DRAG SELECTION BAR ═══════════════ */}
      {dragSel && !dragSel.active && dragSel.startIdx !== dragSel.endIdx && (() => {
        const selUser = users.find(u => u.id === dragSel.userId);
        const lo = Math.min(dragSel.startIdx, dragSel.endIdx);
        const hi = Math.max(dragSel.startIdx, dragSel.endIdx);
        const startMonth = colToMonth(cols[lo]);
        const endMonth = colToMonth(cols[hi]);
        const selMonths = generateMonths(startMonth, endMonth);
        const colCount = hi - lo + 1;
        const label = granularity === 'month'
          ? `${fmtMonth(startMonth)} – ${fmtMonth(endMonth)}`
          : `${cols[lo].label} – ${cols[hi].label}`;
        return (
          <Box sx={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
            bgcolor: TEAL_DARK, color: 'white', px: 2.5, py: 1.5, borderRadius: 3,
            display: 'flex', alignItems: 'center', gap: 2, zIndex: 1400, boxShadow: 6 }}>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', opacity: 0.75 }}>
                {selUser?.displayName || selUser?.name}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                {label}
                <Typography component="span" sx={{ fontSize: '0.72rem', opacity: 0.7, ml: 1, fontWeight: 400 }}>
                  ({colCount} {granularity === 'month' ? 'månader' : granularity === 'week' ? 'veckor' : 'dagar'})
                </Typography>
              </Typography>
            </Box>
            <Button variant="contained" size="small"
              onClick={() => {
                openPlanDialog(dragSel.userId, startMonth, undefined, selMonths[selMonths.length - 1] || endMonth);
                setDragSel(null);
              }}
              sx={{ bgcolor: 'white', color: TEAL_DARK, fontWeight: 700,
                '&:hover': { bgcolor: '#E3F2FD' }, whiteSpace: 'nowrap', px: 2 }}>
              Planera perioden
            </Button>
            <IconButton size="small" onClick={() => setDragSel(null)}
              sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        );
      })()}

      {/* ═══════════════ FIKTIV RESURS DIALOG ═══════════════ */}
      {fiktivDialog && (() => {
        const proj = projects.find(p => p.id === fiktivDialog.projectId);
        const months = fiktivDialog.months || [];
        const isRange = months.length > 1;
        const existingSingle = !isRange ? (parseFloat(proj?.fiktivPlan?.[months[0]]) || 0) : 0;
        const rawVal = parseFloat(fiktivInput) || 0;
        const valPerMonth = isRange && fiktivRangeMode === 'total' && months.length > 0
          ? rawVal / months.length : rawVal;
        const periodLabel = isRange
          ? `${fmtMonth(months[0])} – ${fmtMonth(months[months.length - 1])} (${months.length} mån)`
          : months.length === 1 ? fmtMonth(months[0]) : '';
        const hasAnyExisting = isRange && months.some(m => (parseFloat(proj?.fiktivPlan?.[m]) || 0) > 0);

        return (
          <Dialog open onClose={() => { setFiktivDialog(null); setFiktivDragSel(null); }} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ bgcolor: '#D97706', color: 'white', py: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography fontWeight="bold" variant="subtitle1">Ej tillsatt</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {proj?.name} · {periodLabel}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => { setFiktivDialog(null); setFiktivDragSel(null); }}
                  sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5 }}>
              {/* Mode toggle — only shown for ranges */}
              {isRange && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Hur ska timmarna fördelas?
                  </Typography>
                  <ToggleButtonGroup size="small" exclusive value={fiktivRangeMode}
                    onChange={(_, v) => v && setFiktivRangeMode(v)} fullWidth>
                    <ToggleButton value="per"
                      sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.6 }}>
                      Per månad
                    </ToggleButton>
                    <ToggleButton value="total"
                      sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.6 }}>
                      Totalt (delas lika)
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
              <TextField
                autoFocus fullWidth size="small" type="number"
                label={isRange
                  ? fiktivRangeMode === 'total'
                    ? `Totalt antal timmar (${months.length} mån)`
                    : `Timmar per månad`
                  : 'Timmar'}
                value={fiktivInput}
                onChange={e => setFiktivInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveFiktiv()}
                inputProps={{ min: 0, step: 8 }}
                helperText={
                  isRange && rawVal > 0
                    ? fiktivRangeMode === 'total'
                      ? `${valPerMonth.toFixed(1)}h per månad × ${months.length} = ${rawVal.toFixed(0)}h totalt`
                      : `${rawVal}h × ${months.length} mån = ${(rawVal * months.length).toFixed(0)}h totalt`
                    : !isRange && existingSingle > 0
                      ? `Befintligt: ${existingSingle}h — lämna tomt för att ta bort`
                      : 'Lämna tomt för att ta bort'
                }
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              {(existingSingle > 0 || hasAnyExisting) && (
                <Button color="error"
                  onClick={() => handleSaveFiktiv('')}
                  sx={{ mr: 'auto' }}>
                  Ta bort
                </Button>
              )}
              <Button onClick={() => { setFiktivDialog(null); setFiktivDragSel(null); }}>Avbryt</Button>
              <Button variant="contained" onClick={handleSaveFiktiv} disabled={fiktivSaving}
                sx={{ bgcolor: '#D97706', '&:hover': { bgcolor: '#B45309' } }}>
                {fiktivSaving ? 'Sparar…' : 'Spara'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* ═══════════════ PLANERA DIALOG ═══════════════ */}
      {planDialog && (() => {
        const user = users.find(u => u.id === planDialog.userId);
        const cell = planData[planDialog.userId]?.[planDialog.month] || { capacity: 160, total: 0, entries: [] };
        const remaining = Math.max(0, cell.capacity - cell.total);
        const pct = cell.capacity > 0 ? Math.round((cell.total / cell.capacity) * 100) : 0;
        const colors = utilizationColors(pct);
        const rangeLen = rangeMonths.length;
        const evaledVal = evalFormula(planForm.hoursPerMonth);
        const totalRangeHrs = evaledVal > 0
          ? rangeMonths.reduce((s, m) => s + hrsPerMonthFromUnit(evaledVal, planUnit, m), 0) : 0;
        const unitLabel = planUnit === 'month' ? 'mån' : planUnit === 'week' ? 'vecka' : 'dag';
        const avgMonthly = evaledVal > 0 && planUnit !== 'month'
          ? hrsPerMonthFromUnit(evaledVal, planUnit, planForm.rangeStart || planDialog.month) : 0;

        return (
          <Dialog open onClose={() => setPlanDialog(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: TEAL_DARK, color: 'white', pb: 1.5, pt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography fontWeight="bold" variant="subtitle1">Planera grovtid</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    {user ? (user.displayName || user.name || user.email) : 'Välj resurs'} &mdash; {planDialog.month ? fmtMonth(planDialog.month) : ''}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setPlanDialog(null)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
              {planDialog.userId && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f7fa', borderRadius: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
                    {[
                      { label: 'Kapacitet', val: `${cell.capacity}h` },
                      { label: 'Planerat',  val: `${cell.total.toFixed(1)}h`, color: colors.text },
                      { label: 'Kvar',      val: `${remaining.toFixed(0)}h`, color: remaining > 0 ? '#2E7D32' : '#C62828' },
                    ].map(({ label, val, color }) => (
                      <Box key={label}>
                        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                        <Typography fontWeight="bold" sx={{ color: color || 'text.primary' }}>{val}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ height: 6, bgcolor: '#dce8f5', borderRadius: 3, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${Math.min(pct, 100)}%`,
                      bgcolor: colors.bar, borderRadius: 3, transition: 'width 0.3s' }} />
                  </Box>
                </Box>
              )}

              {cell.entries.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.8 }}>
                    Befintliga poster
                  </Typography>
                  {cell.entries.map((e, i) => {
                    const sc = STATUS_META[e.status] || STATUS_META.offertfas;
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, borderBottom: '1px solid #f0f0f0' }}>
                        <Chip label={sc.label} size="small"
                          sx={{ fontSize: '0.6rem', height: 16, bgcolor: sc.bg, color: sc.color }} />
                        <Typography sx={{ flex: 1, fontSize: '0.8rem' }}>
                          {e.projectName}
                          {e.projectNumber && <span style={{ color: '#999', marginLeft: 5, fontSize: '0.7rem' }}>{e.projectNumber}</span>}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: sc.color }}>{e.hours.toFixed(1)}h</Typography>
                        <Chip label={e.type === 'budget' ? 'Budget' : 'Grovplan'} size="small"
                          sx={{ fontSize: '0.58rem', height: 15, bgcolor: e.type === 'budget' ? '#E3F2FD' : '#EDE7F6' }} />
                        <Tooltip title="Öppna uppdrag">
                          <IconButton size="small" onClick={() => navigate(`/projects/${e.projectId}`)}
                            sx={{ p: 0.3, color: 'text.disabled', '&:hover': { color: TEAL } }}>
                            <OpenInNewIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                        {e.type === 'rough' && (
                          <IconButton size="small"
                            onClick={() => handleDeleteRoughEntry(e.projectId, e.roughId)}
                            sx={{ p: 0.2, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Typography variant="caption" fontWeight="bold" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                Lägg till grovplanering
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Autocomplete
                    size="small"
                    options={activeProjects}
                    getOptionLabel={p => `${p.name || ''}${p.projectNumber ? ` · ${p.projectNumber}` : ''}`}
                    value={selectedProject || null}
                    onChange={(_, p) => setPlanForm(f => ({ ...f, projectId: p?.id || '' }))}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    noOptionsText="Inga uppdrag matchar"
                    renderInput={params => (
                      <TextField {...params} label="Sök och välj uppdrag" placeholder="Namn eller uppdragsnummer…" />
                    )}
                    renderOption={(props, p) => {
                      const sc = STATUS_META[p.status] || STATUS_META.offertfas;
                      return (
                        <li {...props} key={p.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sc.color, flexShrink: 0 }} />
                            <Box>
                              <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>{p.name}</Typography>
                              {p.projectNumber && (
                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{p.projectNumber}</Typography>
                              )}
                            </Box>
                            <Chip label={sc.label} size="small"
                              sx={{ ml: 'auto', fontSize: '0.58rem', height: 15, bgcolor: sc.bg, color: sc.color }} />
                          </Box>
                        </li>
                      );
                    }}
                  />
                  {selectedProject && (() => {
                    const hasBudget = (selectedProject.budgets || []).some(
                      b => (b.resources?.length > 0) || (b.activities?.length > 0)
                    );
                    if (hasBudget) {
                      return (
                        <Box sx={{ mt: 0.8, p: 1, bgcolor: '#E3F2FD', borderRadius: 1, border: '1px solid #90CAF9',
                          display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: '0.75rem', color: TEAL_DARK, flex: 1 }}>
                            Budget finns — timmar syns automatiskt i FramtidN.
                          </Typography>
                          <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                            onClick={() => navigate(`/projects/${selectedProject.id}`)}
                            sx={{ fontSize: '0.72rem', color: TEAL_DARK, textTransform: 'none',
                              p: '2px 8px', border: `1px solid ${TEAL}`, borderRadius: 1, whiteSpace: 'nowrap' }}>
                            Öppna budget
                          </Button>
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ mt: 0.8, p: 1.2, bgcolor: '#FFF8E1', borderRadius: 1, border: '1px solid #FFE082' }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#7B5100', mb: 0.8 }}>
                          Ingen budget finns. Skapa en för noggrannare planering.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button size="small" variant="contained"
                            startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                            onClick={() => navigate(`/projects/${selectedProject.id}?tab=budget`)}
                            sx={{ fontSize: '0.72rem', textTransform: 'none', p: '3px 10px',
                              bgcolor: '#F57C00', '&:hover': { bgcolor: '#E65100' } }}>
                            Skapa budget
                          </Button>
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>

                {/* Range picker — start from planForm.rangeStart so past months from grid selection are included */}
                {(() => {
                  const pickerStart = planForm.rangeStart || getDefaultWindowStart('month');
                  const pickerMonths = generateMonthsFrom(pickerStart, 36);
                  return (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36 }}>Från</Typography>
                      <Select size="small" value={planForm.rangeStart}
                        onChange={e => setPlanForm(f => ({
                          ...f, rangeStart: e.target.value,
                          rangeEnd: f.rangeEnd < e.target.value ? e.target.value : f.rangeEnd,
                        }))}
                        sx={{ flex: 1, fontSize: '0.8rem' }}>
                        {pickerMonths.map(m => <MenuItem key={m} value={m}>{fmtMonth(m)}</MenuItem>)}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 22, textAlign: 'center' }}>→</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 24 }}>till</Typography>
                      <Select size="small" value={planForm.rangeEnd}
                        onChange={e => setPlanForm(f => ({ ...f, rangeEnd: e.target.value }))}
                        sx={{ flex: 1, fontSize: '0.8rem' }}>
                        {pickerMonths.filter(m => m >= planForm.rangeStart).map(m => (
                          <MenuItem key={m} value={m}>{fmtMonth(m)}</MenuItem>
                        ))}
                      </Select>
                    </Box>
                  );
                })()}

                {/* Hours input + unit toggle */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField size="small"
                    label={`Tim / ${unitLabel}`}
                    value={planForm.hoursPerMonth}
                    onChange={e => setPlanForm(f => ({ ...f, hoursPerMonth: e.target.value }))}
                    placeholder={`t.ex. ${planUnit === 'week' ? '25 eller 50/2' : planUnit === 'day' ? '3' : '80'}`}
                    error={!!planForm.hoursPerMonth && evaledVal === null}
                    helperText={
                      planForm.hoursPerMonth && evaledVal === null
                        ? 'Ogiltigt uttryck'
                        : evaledVal > 0 && planForm.hoursPerMonth !== String(evaledVal)
                          ? `= ${evaledVal % 1 === 0 ? evaledVal.toFixed(0) : evaledVal.toFixed(2)} tim/${unitLabel}`
                          : ''
                    }
                    sx={{ flex: 1 }} />
                  <ToggleButtonGroup size="small" exclusive value={planUnit}
                    onChange={(_, v) => v && setPlanUnit(v)}
                    sx={{ alignSelf: 'flex-start', mt: 0.1,
                      '& .MuiToggleButton-root': { py: 0.6, px: 1.1, fontSize: '0.7rem', textTransform: 'none' } }}>
                    <ToggleButton value="month">/ mån</ToggleButton>
                    <ToggleButton value="week">/ vecka</ToggleButton>
                    <ToggleButton value="day">/ dag</ToggleButton>
                  </ToggleButtonGroup>
                  {planDialog.userId && (
                    <Button size="small" variant="outlined"
                      onClick={handleFillUp}
                      sx={{ whiteSpace: 'nowrap', fontSize: '0.72rem', borderColor: TEAL, color: TEAL,
                        alignSelf: 'flex-start', mt: 0.1, py: 0.55 }}>
                      Fyll upp
                    </Button>
                  )}
                </Box>

                {evaledVal > 0 && (
                  <Box sx={{ p: 1, bgcolor: '#EFF6FF', borderRadius: 1, border: '1px solid #BFDBFE' }}>
                    {planUnit !== 'month' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3 }}>
                        {evaledVal % 1 === 0 ? evaledVal.toFixed(0) : evaledVal.toFixed(2)} tim/{unitLabel}
                        {' → ≈ '}{avgMonthly.toFixed(0)} tim/mån
                        {planUnit === 'week' ? ` (${(workdaysInMonth(planForm.rangeStart || planDialog.month) / 5).toFixed(1)} veckor)` : ` (${workdaysInMonth(planForm.rangeStart || planDialog.month)} arb.dagar)`}
                      </Typography>
                    )}
                    <Typography variant="caption" color={TEAL_DARK} fontWeight="bold">
                      {rangeLen === 1
                        ? `${totalRangeHrs.toFixed(0)} tim för ${fmtMonth(planForm.rangeStart)}`
                        : `${totalRangeHrs.toFixed(0)} tim totalt · ${rangeLen} månader (${fmtMonth(planForm.rangeStart)} – ${fmtMonth(planForm.rangeEnd)})`}
                    </Typography>
                  </Box>
                )}

                <TextField size="small" label="Notering (valfri)"
                  value={planForm.note}
                  onChange={e => setPlanForm(f => ({ ...f, note: e.target.value }))} />
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setPlanDialog(null)}>Stäng</Button>
              <Button variant="contained"
                onClick={handleSavePlan}
                disabled={saving || !planForm.projectId || !evaledVal || evaledVal <= 0}
                sx={{ bgcolor: TEAL_DARK, '&:hover': { bgcolor: '#013a6b' } }}>
                {saving ? 'Sparar...' : rangeLen > 1 ? `Spara ${rangeLen} månader` : 'Spara'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Box>
  );
}
